import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Smartphone, CheckCircle2, AlertCircle, ExternalLink } from 'lucide-react'
import { useLatestSystemVersion } from '@/hooks/useLatestSystemVersion'
import { useAllBroadcastDevices } from '@/hooks/useAllBroadcastDevices'
import { useNavigate, useLocation } from 'react-router-dom'

export default function AppVersionCard() {
  const navigate = useNavigate()
  const location = useLocation()
  const { devices, isLoading: devicesLoading } = useAllBroadcastDevices()
  const { latestVersion, isLoading: versionLoading } = useLatestSystemVersion()

  // 計算版本更新狀態
  const updatedDeviceCount = devices.filter(
    (device) => device.app_version && latestVersion && device.app_version === latestVersion.version,
  ).length
  const notUpdatedDeviceCount = devices.length - updatedDeviceCount

  // war-room 可掛在 /war-room（sys_admin）或 /provider/:id/war-room（provider）；
  // 跳到 device-management 時要保留同個 scope。
  const providerMatch = location.pathname.match(/^\/provider\/(\d+)\//)
  const devicePathPrefix = providerMatch ? `/provider/${providerMatch[1]}` : '/admin'

  const handleViewAllDevices = () => {
    navigate(`${devicePathPrefix}/device-management`)
  }

  const handleViewOutdatedDevices = () => {
    navigate(`${devicePathPrefix}/device-management?versionFilter=outdated`)
  }

  return (
    <div className="fixed bottom-4 left-4 z-10">
      <Card className="bg-white/95 backdrop-blur-sm shadow-lg hover:shadow-xl transition-shadow">
        <CardContent className="p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Smartphone className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-600">APP 版本及更新狀況</h3>
              <p className="text-xs text-gray-500">
                最新版本號：{versionLoading ? '載入中...' : latestVersion?.version || '無資料'}
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <span className="text-sm text-gray-600">已更新總數</span>
              </div>
              <span className="text-sm font-semibold text-gray-900">
                {devicesLoading ? '-' : updatedDeviceCount} 台裝置
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-amber-500" />
                <span className="text-sm text-gray-600">未更新總量</span>
              </div>
              <Button
                variant="link"
                size="sm"
                className="h-auto p-0 text-sm font-semibold text-amber-600 hover:text-amber-700"
                onClick={handleViewOutdatedDevices}
                disabled={devicesLoading || notUpdatedDeviceCount === 0}
              >
                {devicesLoading ? '-' : notUpdatedDeviceCount} 台裝置
                {!devicesLoading && notUpdatedDeviceCount > 0 && (
                  <ExternalLink className="ml-1 h-3 w-3" />
                )}
              </Button>
            </div>
            {!devicesLoading && devices.length > 0 && (
              <div className="mt-2 pt-2 border-t">
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>更新進度</span>
                  <span>{Math.round((updatedDeviceCount / devices.length) * 100)}%</span>
                </div>
                <div className="mt-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-green-500 to-green-600 transition-all duration-500"
                    style={{ width: `${(updatedDeviceCount / devices.length) * 100}%` }}
                  />
                </div>
              </div>
            )}
            <div className="mt-3 pt-3 border-t">
              <Button variant="outline" size="sm" className="w-full" onClick={handleViewAllDevices}>
                <Smartphone className="mr-2 h-4 w-4" />
                查看所有裝置
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
